import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NotebookPen, Save, Info } from "lucide-react";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.entries || d?.results || []);

const painLabel = (n) =>
  n === 0 ? "Nessun dolore" : n <= 3 ? "Dolore lieve" : n <= 6 ? "Dolore moderato" : "Dolore intenso";
const effortLabel = (n) =>
  n === 0 ? "Nessuno sforzo" : n <= 2 ? "Sforzo leggero" : n <= 5 ? "Sforzo moderato" : n <= 7 ? "Sforzo intenso" : "Sforzo massimale";

const InfoPopover = ({ title, testId, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" aria-label={`Info ${title}`} data-testid={testId}
        className="grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-primary transition-colors hover:bg-primary/20">
        <Info className="h-3.5 w-3.5" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-80 text-sm" data-testid={`${testId}-content`}>
      <p className="mb-1.5 font-bold text-[#1f3a5f]">{title}</p>
      {children}
    </PopoverContent>
  </Popover>
);

const Scale = ({ title, subtitle, value, onChange, label, testId, info }) => (
  <div>
    <div className="flex items-center gap-2">
      <p className="text-lg font-bold text-[#1f3a5f]">{title}</p>
      {info}
    </div>
    <p className="mb-3 text-xs text-muted-foreground">{subtitle}</p>
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {Array.from({ length: 11 }).map((_, n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`h-10 w-10 rounded-xl border text-sm font-semibold transition-all ${
            value === n ? "border-[#5f9c96] bg-[#3f9a72] text-white" : "border-border bg-white text-[#1f3a5f] hover:border-primary/40"
          }`}
          data-testid={`${testId}-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
    <div className="mt-3 rounded-xl border border-[#cfe6d8] bg-[#eef7f1] px-3 py-2 text-sm font-medium text-[#3f7a70]">
      <span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#3f9a72]" />
      {value} — {label(value)}
    </div>
  </div>
);

export default function Diary() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pain, setPain] = useState(0);
  const [effort, setEffort] = useState(0);
  const [sensation, setSensation] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setEntries(asList(await api.diary(user.id))); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.createDiary({
        pain_nrs: pain,
        difficulty: effort,
        sensation: sensation || undefined,
        notes: notes || undefined,
      });
      if ((pain > 7 || effort > 7) && user?.physio_id) {
        try {
          await api.sendMessage({
            receiver_id: user.physio_id,
            content: `⚠️ Alert automatico dal diario di ${user.full_name || user.username}: dolore ${pain}/10, sforzo ${effort}/10 (${new Date().toLocaleDateString("it-IT")}).${notes ? ` Note: ${notes}` : ""}`,
          });
          toast.warning("Valori elevati: il tuo fisioterapista è stato avvisato automaticamente.");
        } catch { /* alert non bloccante */ }
      }
      toast.success("Voce salvata");
      setPain(0); setEffort(0); setSensation(""); setNotes(""); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Diario" subtitle="Tieni traccia del tuo benessere quotidiano." />

      <Card className="mb-8 border-border/70">
        <CardContent className="space-y-6 p-6">
          <p className="text-xl font-bold text-[#1f3a5f]">Nuova voce</p>
          <Scale title="Dolore" subtitle="Scala NRS · Numeric Rating Scale" value={pain} onChange={setPain} label={painLabel} testId="diary-pain"
            info={<InfoPopover title="Scala NRS (Numeric Rating Scale)" testId="info-nrs">
              <p className="text-muted-foreground">Misura l'intensità del <b>dolore</b> da <b>0</b> a <b>10</b>:</p>
              <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                <li><b>0</b> — nessun dolore</li>
                <li><b>1–3</b> — dolore lieve</li>
                <li><b>4–6</b> — dolore moderato</li>
                <li><b>7–10</b> — dolore intenso</li>
              </ul>
              <p className="mt-1 text-muted-foreground">Scegli il numero che corrisponde al dolore che senti in questo momento.</p>
            </InfoPopover>} />
          <Scale title="Sforzo percepito" subtitle="Scala di Borg CR10 · sforzo percepito" value={effort} onChange={setEffort} label={effortLabel} testId="diary-effort"
            info={<InfoPopover title="Scala di Borg CR10" testId="info-borg">
              <p className="text-muted-foreground">Misura lo <b>sforzo percepito</b> durante l'attività da <b>0</b> a <b>10</b>:</p>
              <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                <li><b>0</b> — nessuno sforzo (riposo)</li>
                <li><b>1–2</b> — sforzo leggero</li>
                <li><b>3–5</b> — sforzo moderato</li>
                <li><b>6–7</b> — sforzo intenso</li>
                <li><b>8–10</b> — sforzo massimale</li>
              </ul>
              <p className="mt-1 text-muted-foreground">Indica quanto è stato faticoso l'esercizio.</p>
            </InfoPopover>} />
          <div>
            <p className="mb-1.5 font-medium text-[#1f3a5f]">Sensazione dopo esercizi</p>
            <Input value={sensation} onChange={(e) => setSensation(e.target.value)} placeholder="es. rilassato, stanco, energico" data-testid="diary-sensation-input" />
          </div>
          <div>
            <p className="mb-1.5 font-medium text-[#1f3a5f]">Note libere</p>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Scrivi qui..." data-testid="diary-note-input" />
          </div>
          <Button className="h-12 w-full text-base" onClick={save} disabled={saving} data-testid="save-diary-button">
            <Save className="mr-2 h-5 w-5" /> {saving ? "Salvataggio..." : "Salva voce"}
          </Button>
        </CardContent>
      </Card>

      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Voci precedenti</p>
      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> :
        entries.length === 0 ? <EmptyState title="Diario vuoto" subtitle="Aggiungi la tua prima voce." /> : (
          <div className="space-y-3" data-testid="diary-list">
            {entries.map((it, i) => (
              <Card key={it.id || it._id || i} className="border-border/70">
                <CardContent className="flex gap-4 p-4">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#e6f0e9] text-[#3f7a70]">
                    <NotebookPen className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-muted-foreground">
                        {new Date(it.created_at || it.date || Date.now()).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                      </p>
                      <div className="flex gap-1.5">
                        {(it.pain_nrs ?? it.pain_level) != null && (
                          <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">Dolore {it.pain_nrs ?? it.pain_level}/10</span>
                        )}
                        {it.difficulty != null && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Sforzo {it.difficulty}/10</span>
                        )}
                      </div>
                    </div>
                    {it.sensation && <p className="mt-1 text-sm font-medium text-[#1f3a5f]">{it.sensation}</p>}
                    <p className="mt-0.5 whitespace-pre-line text-sm text-foreground/85">{it.notes || it.content || it.note || <span className="italic text-muted-foreground">Nessuna nota</span>}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
